import { InfoTooltip } from '../InfoTooltip.js';
import { METRIC_EXPLANATIONS } from '../../data/metric-explanations.js';

export interface FundingAttributionData {
  deployerAddress: string | null;
  fundingSourceAddress: string | null;
  fundingSourceType: string;
  fundingRiskLevel: string;
  fundingConfidence: number;
  fundingSourceLabel: string;
  dataUnavailableReason?: string;
}

const NO_DATA = 'No data available yet';
const FIELD_UNAVAILABLE = 'Not enough public data yet';

function truncAddr(addr: string): string {
  if (addr.length <= 14) return addr;
  return `${addr.slice(0, 8)}...${addr.slice(-6)}`;
}

function typeColor(type: string): string {
  if (type === 'EXCHANGE') return '#27ae60';
  if (type === 'MIXER') return '#e74c3c';
  if (type === 'BRIDGE' || type === 'CONTRACT' || type === 'PRIVATE_WALLET') return '#f39c12';
  return '#888';
}

function riskColor(level: string): string {
  if (level === 'HIGH') return '#e74c3c';
  if (level === 'MEDIUM') return '#f39c12';
  return '#27ae60';
}

export function FundingAttributionPanel({ data }: { data: FundingAttributionData | null | undefined }) {
  const d = data ?? null;

  if (!d) return null;

  if (d.dataUnavailableReason && d.fundingConfidence === 0) {
    return (
      <div className="dash-card ws-forensic-section">
        <h3 className="ws-forensic-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          Deployer Funding Source
          <InfoTooltip explain={METRIC_EXPLANATIONS.fundingAttribution} compact />
        </h3>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Funding Analysis</span>
            <span className="intel-metric-value" style={{ fontSize: '0.85rem' }}>
              {d.dataUnavailableReason || FIELD_UNAVAILABLE}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dash-card ws-forensic-section">
      <h3 className="ws-forensic-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        Deployer Funding Source
        <InfoTooltip explain={METRIC_EXPLANATIONS.fundingAttribution} compact />
      </h3>
      <div className="intel-grid intel-grid-3">
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Funding Source</span>
            <span className="intel-metric-value" style={{ fontSize: '0.85rem' }}>
              {d.fundingSourceLabel || FIELD_UNAVAILABLE}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Source Type</span>
            <span className={`intel-bool-badge`} style={{ backgroundColor: typeColor(d.fundingSourceType), color: '#fff' }}>
              {d.fundingSourceType}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Risk Assessment</span>
            <span className={`intel-bool-badge`} style={{ backgroundColor: riskColor(d.fundingRiskLevel), color: '#fff' }}>
              {d.fundingRiskLevel === 'LOW' ? 'Low Risk Indicators' : d.fundingRiskLevel === 'HIGH' ? 'Severe Risk Indicators' : 'Moderate Risk Indicators'}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Deployer</span>
            <span className="intel-metric-value" style={{ fontSize: '0.75rem' }}>
              {d.deployerAddress ? truncAddr(d.deployerAddress) : NO_DATA}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Source Address</span>
            <span className="intel-metric-value" style={{ fontSize: '0.75rem' }}>
              {d.fundingSourceAddress ? truncAddr(d.fundingSourceAddress) : NO_DATA}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Confidence</span>
            <span className="intel-metric-value">
              {d.fundingConfidence}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
